<script setup>
import { onMounted, ref, watch, nextTick, computed, defineEmits } from 'vue';
import { date } from 'quasar';
import { currentModel } from '../forms/currentModel';
import MapView from '../../../experimental/hybrid-view/MapView.vue';
import { EventBus } from '../../../experimental/hybrid-view/eventBus';
import { Notify, Loading, Dialog } from 'quasar';
import { useRouter } from 'vue-router/composables';
import { xios } from '../forms/tempAxios';

import Forms from '../forms/Forms.vue';

// Define emits
const emit = defineEmits(['creation-success']);

const router = useRouter();
const isLoading = ref(false);
// User info from localStorage
const userInfo = ref(JSON.parse(localStorage.getItem('userInfo')));

const mapRef = ref(null);
const visualPois = ref({});
const dialog = ref(false);
const poiList = ref([]);
const selectedPoi = ref({
  name: '',
  address: '',
  centerLat: null,
  centerLng: null,
  paths: [],
  coords: [],
  meta: { type: '', name: '', addresstype: '' },
});
const editIndex = ref(undefined);
const poiEdit = ref(null);

const basic = ref([
  {
    is: 'TSeparator',
    label: 'Basic Details',
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'Company Name',
        model: 'companyName',
      },
      {
        is: 'TSelect',
        Label: 'Customer Type',
        options: ['corporate', 'individual', 'government', 'non-profit'],
        useInput: true,
        icon: 'keyboard_arrow_down',
        model: 'meta.customerType',
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'Industry',
        model: 'meta.industry',
      },
      {
        is: 'TInput',
        Label: 'Company Registration',
        model: 'companyRegistration',
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'Tax ID',
        model: 'meta.taxId',
      },
      {
        is: 'TInput',
        Label: 'Year Established',
        model: 'meta.yearEstablished',
        type: 'number',
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'Employee Count',
        model: 'meta.employeeCount',
        type: 'number',
      },
      {
        is: 'TInput',
        Label: 'Website',
        model: 'meta.website',
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TSelect',
        Label: 'Status',
        options: [
          { label: 'Active', value: 1 },
          { label: 'Inactive', value: 0 },
          { label: 'Suspended', value: -1 },
        ],
        useInput: true,
        emitValue: true,
        mapOptions: true,
        icon: 'keyboard_arrow_down',
        model: 'status',
      },
    ],
  },
]);

const second = ref([
  {
    is: 'TSeparator',
    label: 'Contact Person Details',
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'Full Name',
        model: 'contactPerson',
      },
      {
        is: 'TInput',
        Label: 'Mobile No',
        model: 'contactPhone',
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'E-mail',
        model: 'contactEmail',
      },
    ],
  },
  {
    is: 'TUploads',
    items: [{ Label: 'Profile Image', model: 'meta.profileImage' }],
  },
  {
    is: 'TSeparator',
    label: 'Address Details',
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'Street',
        model: 'meta.address.street',
      },
      {
        is: 'TInput',
        Label: 'City',
        model: 'meta.address.city',
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'State',
        model: 'meta.address.state',
      },
      {
        is: 'TInput',
        Label: 'Postal Code',
        model: 'meta.address.postalCode',
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'Country',
        model: 'meta.address.country',
      },
    ],
  },
]);

// Add computed property for edit mode
const isEditMode = computed(() => {
  return !!(currentModel.value && currentModel.value.id);
});

// Add page title computed property
const pageTitle = computed(() => {
  return isEditMode.value ? 'Update Customer' : 'Create Customer';
});

function resetForm() {
  currentModel.value = {
    id: null, // Ensure ID is reset
    companyName: '',
    companyRegistration: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    clientId: (userInfo.value && userInfo.value.uid) || 1,
    status: 1,
    meta: {
      customerType: '',
      industry: '',
      employeeCount: null,
      yearEstablished: null,
      website: '',
      taxId: '',
      profileImage: null,
      address: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      },
    },
    pois: [],
  };
  selectedPoi.value = {
    name: '',
    address: '',
    centerLat: null,
    centerLng: null,
    paths: [],
    coords: [],
    meta: { type: '', color: '' },
  };
  poiList.value = [];
  poiEdit.value = null;
  editIndex.value = undefined;
  if (mapRef.value && mapRef.value.clearDrawing) {
    mapRef.value.clearDrawing();
  }
}

onMounted(() => {
  // Check localStorage first
  const storedCustomerId = localStorage.getItem('currentCustomerId');
  if (storedCustomerId) {
    console.log('Found customer ID in localStorage:', storedCustomerId);
    loadCustomerData(storedCustomerId); // Load data
    localStorage.removeItem('currentCustomerId'); // Clear after use
  }
  // Check route params if no localStorage ID
  else if (router.currentRoute.params.id) {
    // Assuming router is available
    console.log(
      'Found customer ID in route params:',
      router.currentRoute.params.id,
    );
    loadCustomerData(router.currentRoute.params.id);
  }
  // Otherwise, it's create mode
  else {
    console.log('No customer ID found, initializing create mode.');
    resetForm();
  }
});

function validateCustomerForm() {
  let errors = [];
  if (!currentModel.value.companyName) {
    errors.push('Company Name is required.');
  }
  if (!currentModel.value.contactPerson) {
    errors.push('Contact Person Full Name is required.');
  }
  if (!currentModel.value.contactPhone) {
    errors.push('Contact Person Mobile No is required.');
  }
  if (!currentModel.value.contactEmail) {
    errors.push('Contact Person E-mail is required.');
  } else if (
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentModel.value.contactEmail)
  ) {
    errors.push('Contact Person E-mail format is invalid.');
  }

  // Check meta.address fields
  if (!currentModel.value.meta || !currentModel.value.meta.address) {
    errors.push('Address information is required.');
  } else {
    const address = currentModel.value.meta.address;
    if (!address.street) {
      errors.push('Street is required.');
    }
    if (!address.city) {
      errors.push('City is required.');
    }
    if (!address.state) {
      errors.push('State is required.');
    }
    if (!address.country) {
      errors.push('Country is required.');
    }
  }

  if (!currentModel.value.pois || currentModel.value.pois.length === 0) {
    errors.push('At least one Point of Interest (POI) must be added.');
  }
  return errors;
}

async function submitCustomerData() {
  const validationErrors = validateCustomerForm();
  if (validationErrors.length > 0) {
    validationErrors.forEach((error) => {
      Notify.create({ message: error, color: 'negative' });
    });
    return;
  }

  isLoading.value = true;
  Loading.show({
    message: isEditMode.value ? 'Updating customer...' : 'Creating customer...',
  });

  try {
    // *** Select endpoint based on mode ***
    const endpoint = isEditMode.value
      ? 'api-tms/script-update-standard'
      : 'api-tms/script-insert-standard';

    // ... cleanup formData ...
    const formData = { ...currentModel.value };
    if (formData.meta && formData.meta.meta) {
      /* ... flatten meta ... */
    }
    const clientId = (userInfo.value && userInfo.value.uid) || 1;

    const payload = {
      model: 'Customer',
      options: {
        ...formData,
        // *** Conditionally include ID only for update ***
        id: isEditMode.value ? currentModel.value.id : undefined,
        clientId: clientId,
        pois: formData.pois || [],
      },
    };
    // Clean up undefined ID if creating
    if (!isEditMode.value) {
      delete payload.options.id;
    }

    console.log(
      `Submitting Customer Payload (${isEditMode.value ? 'Update' : 'Create'}):`,
      JSON.stringify(payload, null, 2),
    );

    const response = await xios.post(endpoint, payload);

    if (response.data.success) {
      Notify.create({
        message: `Customer ${isEditMode.value ? 'updated' : 'created'} successfully!`,
        color: 'positive',
      });
      resetForm();
      Loading.hide();
      emit('creation-success', 'Customer List'); // Navigate back to list
    } else {
      /* ... error handling ... */
    }
  } catch (error) {
    /* ... error handling ... */
  } finally {
    /* ... loading false ... */
  }
}

function cancelCustomerCreation() {
  resetForm();
  emit('creation-success', 'Customer List');
}

async function loadCustomerData(id) {
  if (!id) {
    console.error('loadCustomerData called with no ID');
    return;
  }

  console.log('Loading customer data for ID:', id);
  isLoading.value = true;

  try {
    const response = await xios.post('api-tms/script-pagination-standard', {
      model: 'Customer',
      filterFields: [{ field: 'id', operator: 'eq', value: id }],
      includeModels: [{ model: 'Poi' }],
      page: 1,
      itemsPerPage: 1,
    });

    if (response.data.success && response.data.data.items.length > 0) {
      const customerData = response.data.data.items[0];
      console.log('Customer data retrieved:', customerData);

      // Ensure meta object exists
      if (!customerData.meta) {
        customerData.meta = {};
      }

      // Migrate old address format to new format if needed
      if (!customerData.meta.address) {
        customerData.meta.address = {
          street: customerData.address || '',
          city: customerData.city || '',
          state: customerData.state || '',
          postalCode: customerData.postalCode || '',
          country: customerData.country || '',
        };

        // Clean up old address fields if they exist
        delete customerData.address;
        delete customerData.city;
        delete customerData.state;
        delete customerData.postalCode;
        delete customerData.country;
      }

      // Set the current model with the cleaned-up data
      currentModel.value = customerData;
      poiList.value = customerData.pois || [];

      // Show edit mode notification
      Notify.create({
        message: 'Now editing customer: ' + customerData.companyName,
        color: 'blue-grey',
        timeout: 3000,
      });
    } else {
      Notify.create({ message: 'Customer not found', color: 'negative' });
      emit('creation-success', 'Customer List');
    }
  } catch (error) {
    Notify.create({
      message: 'Failed to load customer data',
      color: 'negative',
    });
    console.error('Error loading customer:', error);
  } finally {
    isLoading.value = false;
  }
}

watch(mapRef, (v) => {
  if (v) {
    setTimeout(() => {
      EventBus.$emit('poiAdd');
      initSearchBox();
    }, 300);
  } else {
    console.log(v);
  }
});

function initSearchBox() {
  const map = mapRef.value.mapRef.$mapObject;
  const input = document.getElementById('pac-input');
  const searchBox = new google.maps.places.SearchBox(input);

  map.addListener('bounds_changed', () => {
    searchBox.setBounds(map.getBounds());
  });

  let markers = [];

  searchBox.addListener('places_changed', () => {
    const places = searchBox.getPlaces();

    if (places.length == 0) {
      return;
    }

    console.log(places);
    markers.forEach((marker) => {
      marker.setMap(null);
    });
    markers = [];

    const bounds = new google.maps.LatLngBounds();

    places.forEach((place) => {
      if (!place.geometry || !place.geometry.location) {
        console.log('Returned place contains no geometry');
        return;
      }

      const icon = {
        url: place.icon,
        size: new google.maps.Size(71, 71),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(17, 34),
        scaledSize: new google.maps.Size(25, 25),
      };

      markers.push(
        new google.maps.Marker({
          map,
          icon,
          title: place.name,
          position: place.geometry.location,
        }),
      );
      if (place.geometry.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry.location);
      }
    });
    map.fitBounds(bounds);
  });
}

function editPOI(poi, index) {
  editIndex.value = index;
  selectedPoi.value = { ...poi };
  if (!selectedPoi.value.meta) {
    selectedPoi.value.meta = { type: '', name: '', addresstype: '' };
  }
  poiEdit.value = { data: poi, index };
  dialog.value = true;
}

async function submitPOI(poi, edit) {
  let poiObj = {};
  poiObj.name = poi.name;
  poiObj.paths = poi.alternateCoords;
  poiObj.coords = JSON.stringify(poi.coords);
  const { lat, lng } = getCenterLatLng(poiObj.paths);
  poiObj.centerLat = lat;
  poiObj.centerLng = lng;
  console.log(lat, lng);
  const geoCode = await reverseGeocode(lat, lng);

  poiObj.address = geoCode.display_name;
  poiObj.address_component = extractAddressComponents(geoCode);
  poiObj.meta = {
    type: poi.meta && poi.meta.type ? poi.meta.type : 'warehouse',
    color: poi.meta && poi.meta.color ? poi.meta.color : '#ffb6c1',
  };

  if (edit) {
    poiEdit.value = null;
    currentModel.value.pois[edit.index] = poiObj;
    return;
  }
  currentModel.value.pois.push(poiObj);
}

function extractAddressComponents(geoCode) {
  const components = [];

  if (geoCode && geoCode.address) {
    // Convert OpenStreetMap format to Google Maps-like format
    const addr = geoCode.address;

    if (addr.road) {
      components.push({
        long_name: addr.road,
        short_name: addr.road,
        types: ['route'],
      });
    }

    if (addr.suburb || addr.neighbourhood) {
      components.push({
        long_name: addr.suburb || addr.neighbourhood,
        short_name: addr.suburb || addr.neighbourhood,
        types: ['neighborhood', 'political'],
      });
    }

    if (addr.city || addr.town || addr.village) {
      components.push({
        long_name: addr.city || addr.town || addr.village,
        short_name: addr.city || addr.town || addr.village,
        types: ['locality', 'political'],
      });
    }

    if (addr.county) {
      components.push({
        long_name: addr.county,
        short_name: addr.county,
        types: ['administrative_area_level_2', 'political'],
      });
    }

    if (addr.state) {
      components.push({
        long_name: addr.state,
        short_name: addr.state,
        types: ['administrative_area_level_1', 'political'],
      });
    }

    if (addr.country) {
      components.push({
        long_name: addr.country,
        short_name: addr.country_code ? addr.country_code.toUpperCase() : '',
        types: ['country', 'political'],
      });
    }

    if (addr.postcode) {
      components.push({
        long_name: addr.postcode,
        short_name: addr.postcode,
        types: ['postal_code'],
      });
    }
  }

  return components;
}

function getCenterLatLng(coordinates) {
  if (!coordinates || coordinates.length === 0) {
    return null;
  }

  const sum = coordinates.reduce(
    (acc, coord) => {
      return {
        lat: acc.lat + coord.lat,
        lng: acc.lng + coord.lng,
      };
    },
    { lat: 0, lng: 0 },
  );

  const center = {
    lat: sum.lat / coordinates.length,
    lng: sum.lng / coordinates.length,
  };
  return center;
}

async function reverseGeocode(lat, lng) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`;
  return await (await fetch(url)).json();
}

function test() {
  console.log(currentModel.value);
}

function removePoi(index) {
  if (
    currentModel.value.pois &&
    index >= 0 &&
    index < currentModel.value.pois.length
  ) {
    currentModel.value.pois.splice(index, 1);
    Notify.create({
      message: 'Point of Interest removed',
      color: 'info',
    });
  }
}

function openAddPoiDialog() {
  editIndex.value = undefined;
  selectedPoi.value = {
    name: '',
    address: '',
    centerLat: null,
    centerLng: null,
    paths: [],
    coords: [],
    meta: {
      type: '',
      name: '',
      addresstype: '',
    },
  };
  dialog.value = true;
}
</script>

<template>
  <div class="fit flex flex-col overflow-auto p-8">
    <div class="flex flex-wrap">
      <div class="w-full lg:w-1/2 flex flex-col gap-4 px-2">
        <div class="text-xl font-semibold text-primary">
          {{ pageTitle }}
        </div>
        <Forms :ui="basic" />
      </div>
      <div class="w-full lg:w-1/2 flex flex-col gap-4 px-2">
        <div class="text-xl font-semibold text-primary opacity-0">
          {{ $attrs.selected }}
        </div>
        <Forms :ui="second" class="flex-grow-0" />
        <q-card>
          <q-card-section class="bg-primary text-white">
            <div class="text-h6 flex justify-between items-center">
              Points of Interest (POI)
              <q-btn
                icon="add_location"
                label="Add POI"
                color="white"
                flat
                dense
                @click="openAddPoiDialog"
              />
            </div>
          </q-card-section>
          <q-card-section>
            <q-list
              bordered
              separator
              v-if="currentModel.pois && currentModel.pois.length > 0"
            >
              <q-item v-for="(poi, index) in currentModel.pois" :key="index">
                <q-item-section>
                  <q-item-label>{{ poi.name }}</q-item-label>
                  <q-item-label caption lines="2">{{
                    poi.address
                  }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <div class="row q-gutter-sm">
                    <!-- <q-btn flat round dense icon="edit" color="primary" @click="editPOI(poi, index)" /> -->
                    <q-btn
                      flat
                      round
                      dense
                      icon="delete"
                      color="negative"
                      @click="removePoi(index)"
                    />
                  </div>
                </q-item-section>
              </q-item>
            </q-list>
            <div v-else class="text-center q-pa-md text-grey">
              No POIs added yet. Click "Add POI" to start.
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <q-dialog
      v-model="dialog"
      persistent
      maximized
      transition-show="slide-up"
      transition-hide="slide-down"
    >
      <q-card class="flex flex-col">
        <q-bar>
          <q-space />
          <q-btn dense flat icon="close" v-close-popup>
            <q-tooltip content-class="bg-white text-primary">Close</q-tooltip>
          </q-btn>
        </q-bar>
        <div class="bg- flex-1 flex flex-col">
          <div class="flex flex-col p-4 gap-2">
            <input
              id="pac-input"
              class="controls"
              type="text"
              placeholder="Search place name or enter exact coordinates"
            />
          </div>
          <div class="flex-1">
            <MapView
              class="fit"
              ref="mapRef"
              :poiEdit="poiEdit"
              @cancelAddPoi="dialog = false"
              :manualPOISubmit="true"
              @submitPOI="submitPOI"
            />
          </div>
        </div>
      </q-card>
    </q-dialog>

    <div class="q-pa-md q-mt-md row justify-end q-gutter-sm">
      <q-btn
        flat
        label="Cancel"
        color="primary"
        @click="cancelCustomerCreation"
      />
      <q-btn
        :label="isEditMode ? 'Update Customer' : 'Create Customer'"
        color="primary"
        @click="submitCustomerData"
        :loading="isLoading"
      />
    </div>
  </div>
</template>

<style>
#description {
  font-family: Roboto;
  font-size: 15px;
  font-weight: 300;
}

#infowindow-content .title {
  font-weight: bold;
}

#infowindow-content {
  display: none;
}

#map #infowindow-content {
  display: inline;
}

.pac-card {
  background-color: #fff;
  border: 0;
  border-radius: 2px;
  box-shadow: 0 1px 4px -1px rgba(0, 0, 0, 0.3);
  margin: 10px;
  padding: 0 0.5em;
  font:
    400 18px Roboto,
    Arial,
    sans-serif;
  overflow: hidden;
  font-family: Roboto;
  padding: 0;
}

#pac-container {
  padding-bottom: 12px;
  margin-right: 12px;
  z-index: 9999999 !important;
}
.pac-container {
  z-index: 9999999 !important;
}

.pac-controls {
  display: inline-block;
  padding: 5px 11px;
}

.pac-controls label {
  font-family: Roboto;
  font-size: 13px;
  font-weight: 300;
}

#pac-input {
  background-color: #fff;
  font-family: Roboto;
  font-size: 15px;
  font-weight: 300;
  margin-left: 12px;
  padding: 0 11px 0 13px;
  text-overflow: ellipsis;
  width: 400px;
}

#pac-input:focus {
  border-color: #4d90fe;
}

#title {
  color: #fff;
  background-color: #4d90fe;
  font-size: 25px;
  font-weight: 500;
  padding: 6px 12px;
}

#target {
  width: 345px;
}
</style>
